import { Dropdown, DropdownContent, DropdownTrigger } from '@waymail/ui';

/*
 * Menu de contexto fino, em cima do Dropdown do design system: nao ha
 * primitivo proprio e nenhuma dependencia nova entra. O Radix cuida do que
 * importa - setas, Home/End, digitar para saltar, Esc, foco preso no menu e
 * colisao com a borda da tela. Aqui so se ancora o menu num ponto da tela: o
 * gatilho e um quadrado invisivel de um pixel no cursor.
 */

export interface PontoDoMenu {
  x: number;
  y: number;
}

export function pontoDoElemento(elemento: Element): PontoDoMenu {
  const r = elemento.getBoundingClientRect();
  return { x: r.left + Math.min(24, r.width / 2), y: r.top + Math.min(24, r.height / 2) };
}

/** Onde o cursor estava. Tecla de menu manda 0,0: ai vale o canto do elemento. */
export function pontoDoEvento(
  e: { clientX: number; clientY: number },
  elemento: Element,
): PontoDoMenu {
  if (e.clientX !== 0 || e.clientY !== 0) return { x: e.clientX, y: e.clientY };
  return pontoDoElemento(elemento);
}

/** Shift+F10 e a tecla de menu: o que o teclado tem no lugar do botao direito. */
export function ehTeclaDeMenu(e: { key: string; shiftKey: boolean }): boolean {
  return e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10');
}

export function MenuDeContexto({
  ponto,
  rotulo,
  onFechar,
  children,
}: {
  ponto: PontoDoMenu | null;
  rotulo: string;
  onFechar: () => void;
  children: React.ReactNode;
}) {
  if (ponto === null) return null;

  return (
    // Outra chave, outro ancora: o Radix mede o gatilho ao montar, e um menu
    // aberto num segundo ponto precisa ser medido de novo.
    <Dropdown
      key={`${ponto.x}:${ponto.y}`}
      open
      onOpenChange={(aberto) => {
        if (!aberto) onFechar();
      }}
    >
      <DropdownTrigger asChild>
        <span
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: ponto.x,
            top: ponto.y,
            width: 1,
            height: 1,
            pointerEvents: 'none',
            overflow: 'hidden',
            color: 'transparent',
            fontSize: 1,
          }}
        >
          {rotulo}
        </span>
      </DropdownTrigger>
      {/* O Radix nomeia o menu pelo gatilho (aria-labelledby): por isso o texto acima. */}
      <DropdownContent
        sideOffset={2}
        // Altura limitada ao espaco real ate a borda (Radix inverte o lado e
        // encolhe); o resto rola por dentro, nada fica fora da tela.
        collisionPadding={8}
        className="max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto"
        // O foco fica onde estava (a lista, a barra): o gatilho e invisivel.
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {children}
      </DropdownContent>
    </Dropdown>
  );
}
